import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { Routes, Route, NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Globe, Newspaper, Image as ImageIcon, Users, Mail,
  CalendarCheck, BarChart3, Search as SearchIcon, ShieldCheck, Settings as SettingsIcon,
  ScrollText, Bell, ExternalLink, LogOut, Eye, MoreHorizontal, X,
} from "lucide-react";
import { api, fmtDate } from "./api";
import Login from "./Login";
import Dashboard from "./Dashboard";
import WebsiteEditor from "./WebsiteEditor";
import BlogManager, { BlogEditor } from "./BlogManager";
import MediaLibrary from "./MediaLibrary";
import Leads, { LeadProfile } from "./Leads";
import Messages from "./Messages";
import Consultations from "./Consultations";
import Analytics from "./Analytics";
import SeoManager from "./SeoManager";
import UsersPage from "./Users";
import Settings from "./Settings";
import ActivityLog from "./ActivityLog";

const AuthContext = createContext(null);
export const useAdmin = () => useContext(AuthContext);

export const ROLE_PERMS = {
  super_admin: ["*"],
  aviah_admin: ["dashboard", "analytics", "leads", "messages", "consultations", "content", "blog", "media", "seo", "settings", "notifications", "search", "users"],
  editor: ["dashboard", "content", "blog", "media", "notifications", "search"],
  viewer: ["dashboard", "analytics", "leads", "messages", "consultations", "notifications"],
};

export const can = (role, perm) =>
  ROLE_PERMS[role]?.includes("*") || ROLE_PERMS[role]?.includes(perm);

const MENU = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, perm: "dashboard", end: true },
  { to: "/admin/website", label: "Website", icon: Globe, perm: "content" },
  { to: "/admin/blog", label: "Blog", icon: Newspaper, perm: "blog" },
  { to: "/admin/media", label: "Media", icon: ImageIcon, perm: "media" },
  { to: "/admin/leads", label: "Leads", icon: Users, perm: "leads" },
  { to: "/admin/messages", label: "Messages", icon: Mail, perm: "messages" },
  { to: "/admin/consultations", label: "Consultations", icon: CalendarCheck, perm: "consultations" },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3, perm: "analytics" },
  { to: "/admin/seo", label: "SEO", icon: SearchIcon, perm: "seo" },
  { to: "/admin/users", label: "Users", icon: ShieldCheck, perm: "users" },
  { to: "/admin/settings", label: "Settings", icon: SettingsIcon, perm: "settings" },
  { to: "/admin/activity", label: "Activity Log", icon: ScrollText, perm: "audit" },
];

// Mobile bottom navigation: the four highest-priority sections + More.
const BOTTOM = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, perm: "dashboard", end: true },
  { to: "/admin/leads", label: "Leads", icon: Users, perm: "leads" },
  { to: "/admin/messages", label: "Messages", icon: Mail, perm: "messages" },
  { to: "/admin/website", label: "Content", icon: Globe, perm: "content" },
];

const HeaderBar = ({ user, onLogout }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [notifs, setNotifs] = useState(null);
  const [openNotifs, setOpenNotifs] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (q.trim().length < 2) {
      setResults(null);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/admin/search?q=${encodeURIComponent(q)}`);
        setResults(data.results);
      } catch {
        setResults([]);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const toggleNotifs = async () => {
    if (!openNotifs) {
      try {
        const { data } = await api.get("/admin/notifications");
        setNotifs(data);
      } catch {
        setNotifs([]);
      }
    }
    setOpenNotifs(!openNotifs);
  };

  const unread = (notifs || []).filter((n) => !n.read).length;

  return (
    <header className="sticky top-0 z-40 flex min-h-16 items-center gap-3 border-b border-[#171717]/10 bg-[#F4F0E8]/95 px-4 pt-[env(safe-area-inset-top)] backdrop-blur-md sm:px-5">
      <img src="/assets/logo-white.webp" alt="Aviah Financial" className="h-5 w-auto rounded-[2px] bg-[#171717] p-1 lg:hidden" />
      <div className="relative hidden flex-1 sm:block">
        <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#171717]/35" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search pages, articles, leads, media…"
          data-testid="admin-search"
          className="w-full max-w-md rounded-[3px] border border-[#171717]/12 bg-white py-2 pl-9 pr-8 text-sm text-[#171717] placeholder:text-[#171717]/35 focus:border-[#C6A46A] focus:outline-none"
        />
        {q && (
          <button
            onClick={() => { setQ(""); setResults(null); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[#171717]/40 hover:text-[#171717]"
            aria-label="Clear search"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
        {results && (
          <div className="absolute left-0 top-full mt-2 w-full max-w-md rounded-[3px] border border-[#171717]/10 bg-white shadow-xl" data-testid="admin-search-results">
            {results.length === 0 && <p className="px-4 py-3 text-xs text-[#171717]/45">No results.</p>}
            {results.map((r, i) => (
              <button
                key={i}
                onClick={() => { setQ(""); setResults(null); navigate(r.to); }}
                className="flex min-h-[44px] w-full items-center gap-3 border-b border-[#171717]/5 px-4 py-2.5 text-left text-sm text-[#171717]/80 hover:bg-[#F4F0E8]"
              >
                <span className="text-[0.58rem] font-medium uppercase tracking-[0.14em] text-[#8a6f3e]">{r.type}</span>
                <span className="truncate">{r.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="ml-auto flex items-center gap-2">
        <a
          href="/?preview=draft"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="admin-preview-site"
          className="hidden items-center gap-2 rounded-[3px] border border-[#171717]/15 px-3 py-2 text-[0.62rem] font-medium uppercase tracking-[0.14em] text-[#171717]/70 transition-colors hover:border-[#C6A46A] hover:text-[#8a6f3e] sm:inline-flex"
        >
          <Eye className="h-3.5 w-3.5" /> Preview Website
        </a>
        <a
          href="/"
          target="_blank"
          rel="noopener noreferrer"
          className="hidden items-center gap-2 rounded-[3px] border border-[#171717]/15 px-3 py-2 text-[0.62rem] font-medium uppercase tracking-[0.14em] text-[#171717]/70 transition-colors hover:border-[#C6A46A] hover:text-[#8a6f3e] md:inline-flex"
        >
          <ExternalLink className="h-3.5 w-3.5" /> Live Site
        </a>

        <div className="relative">
          <button
            onClick={toggleNotifs}
            data-testid="admin-notifications"
            className="relative flex min-h-[44px] min-w-[44px] items-center justify-center rounded-[3px] border border-[#171717]/15 text-[#171717]/70 transition-colors hover:border-[#C6A46A]"
            aria-label="Notifications"
          >
            <Bell className="h-4 w-4" />
            {unread > 0 && <span className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full bg-[#C6A46A]" />}
          </button>
          {openNotifs && (
            <div className="fixed inset-x-3 top-20 z-50 rounded-[3px] border border-[#171717]/10 bg-white shadow-xl sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2 sm:w-80">
              <div className="flex items-center justify-between border-b border-[#171717]/8 px-4 py-3">
                <span className="text-[0.6rem] font-medium uppercase tracking-[0.16em] text-[#171717]/55">Notifications</span>
                <button
                  className="min-h-[36px] text-[0.6rem] uppercase tracking-[0.14em] text-[#8a6f3e] hover:underline"
                  onClick={async () => { await api.post("/admin/notifications/read-all"); toggleNotifs(); toggleNotifs(); }}
                >
                  Mark all read
                </button>
              </div>
              <div className="max-h-[60vh] overflow-y-auto sm:max-h-80">
                {(notifs || []).length === 0 && <p className="px-4 py-5 text-xs text-[#171717]/45">No notifications yet.</p>}
                {(notifs || []).map((n) => (
                  <div key={n.id} className={`border-b border-[#171717]/5 px-4 py-3 ${n.read ? "opacity-55" : ""}`}>
                    <p className="text-xs leading-relaxed text-[#171717]/80">{n.text}</p>
                    <p className="mt-1 text-[0.6rem] text-[#171717]/40">{fmtDate(n.ts)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="ml-1 flex items-center gap-3 border-l border-[#171717]/10 pl-3">
          <div className="hidden text-right sm:block">
            <p className="text-xs font-normal text-[#171717]">{user.name}</p>
            <p className="text-[0.58rem] uppercase tracking-[0.14em] text-[#8a6f3e]">{user.roleLabel}</p>
          </div>
          <button
            onClick={onLogout}
            data-testid="admin-logout"
            className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-[3px] border border-[#171717]/15 text-[#171717]/60 transition-colors hover:border-[#C6A46A] hover:text-[#8a6f3e]"
            aria-label="Sign out"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </header>
  );
};

// Mobile bottom bar with safe-area support + "More" sheet for the rest.
const BottomNav = ({ role }) => {
  const [moreOpen, setMoreOpen] = useState(false);
  const location = useLocation();
  useEffect(() => setMoreOpen(false), [location.pathname]);

  const moreItems = MENU.filter(
    (m) => !BOTTOM.some((b) => b.to === m.to) && can(role, m.perm),
  );

  return (
    <>
      <nav
        className="fixed inset-x-0 bottom-0 z-40 border-t border-[#F4F0E8]/10 bg-[#171717] pb-[env(safe-area-inset-bottom)] lg:hidden"
        aria-label="Admin mobile"
        data-testid="admin-bottom-nav"
      >
        <div className="grid grid-cols-5">
          {BOTTOM.filter((m) => can(role, m.perm)).map((m) => (
            <NavLink
              key={m.to}
              to={m.to}
              end={m.end}
              data-testid={`bottom-nav-${m.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex min-h-[60px] flex-col items-center justify-center gap-1 text-[0.55rem] uppercase tracking-[0.12em] transition-colors ${
                  isActive ? "text-[#C6A46A]" : "text-[#F4F0E8]/50"
                }`
              }
            >
              <m.icon className="h-5 w-5" strokeWidth={1.5} />
              {m.label}
            </NavLink>
          ))}
          <button
            onClick={() => setMoreOpen(true)}
            data-testid="bottom-nav-more"
            className="flex min-h-[60px] flex-col items-center justify-center gap-1 text-[0.55rem] uppercase tracking-[0.12em] text-[#F4F0E8]/50"
          >
            <MoreHorizontal className="h-5 w-5" strokeWidth={1.5} />
            More
          </button>
        </div>
      </nav>

      {moreOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true" data-testid="admin-more-sheet">
          <div className="absolute inset-0 bg-[#171717]/60 backdrop-blur-sm" onClick={() => setMoreOpen(false)} />
          <div className="absolute inset-x-0 bottom-0 rounded-t-xl border-t border-[#C6A46A]/30 bg-[#171717] p-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))]">
            <div className="mb-4 flex items-center justify-between">
              <span className="text-[0.6rem] font-medium uppercase tracking-[0.2em] text-[#C6A46A]">More</span>
              <button onClick={() => setMoreOpen(false)} aria-label="Close" className="flex min-h-[44px] min-w-[44px] items-center justify-center text-[#F4F0E8]/60">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {moreItems.map((m) => (
                <NavLink
                  key={m.to}
                  to={m.to}
                  end={m.end}
                  className={({ isActive }) =>
                    `flex min-h-[52px] items-center gap-3 rounded-[3px] border px-4 text-[0.68rem] uppercase tracking-[0.14em] ${
                      isActive
                        ? "border-[#C6A46A] text-[#C6A46A]"
                        : "border-[#F4F0E8]/12 text-[#F4F0E8]/70"
                    }`
                  }
                >
                  <m.icon className="h-4 w-4" strokeWidth={1.5} />
                  {m.label}
                </NavLink>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const AdminShell = ({ user, onLogout }) => (
  <div className="flex min-h-screen bg-[#F4F0E8]" data-testid="admin-shell">
    <aside className="fixed inset-y-0 left-0 z-40 hidden w-60 flex-col bg-[#171717] lg:flex">
      <div className="flex h-16 items-center border-b border-[#F4F0E8]/10 px-5">
        <img src="/assets/logo-white.webp" alt="Aviah Financial" className="h-6 w-auto" />
      </div>
      <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-5" aria-label="Admin">
        {MENU.filter((m) => can(user.role, m.perm)).map((m) => (
          <NavLink
            key={m.to}
            to={m.to}
            end={m.end}
            data-testid={`admin-nav-${m.label.toLowerCase().replace(/\s/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-[3px] px-3 py-2.5 text-[0.72rem] uppercase tracking-[0.14em] transition-colors ${
                isActive
                  ? "bg-[#292929] text-[#C6A46A]"
                  : "text-[#F4F0E8]/55 hover:bg-[#1E1E1E] hover:text-[#F4F0E8]"
              }`
            }
          >
            <m.icon className="h-4 w-4" strokeWidth={1.5} />
            {m.label}
          </NavLink>
        ))}
      </nav>
      <p className="border-t border-[#F4F0E8]/10 px-5 py-4 text-[0.55rem] uppercase tracking-[0.2em] text-[#F4F0E8]/25">
        Aviah Control Center
      </p>
    </aside>

    <div className="flex min-w-0 flex-1 flex-col lg:ml-60">
      <HeaderBar user={user} onLogout={onLogout} />
      <main className="flex-1 p-4 pb-28 sm:p-8 lg:pb-8">
        <Routes>
          <Route index element={<Dashboard />} />
          <Route path="website" element={<WebsiteEditor />} />
          <Route path="blog" element={<BlogManager />} />
          <Route path="blog/:id" element={<BlogEditor />} />
          <Route path="media" element={<MediaLibrary />} />
          <Route path="leads" element={<Leads />} />
          <Route path="leads/:id" element={<LeadProfile />} />
          <Route path="messages" element={<Messages />} />
          <Route path="consultations" element={<Consultations />} />
          <Route path="analytics" element={<Analytics />} />
          <Route path="seo" element={<SeoManager />} />
          <Route path="users" element={<UsersPage />} />
          <Route path="settings" element={<Settings />} />
          <Route path="activity" element={<ActivityLog />} />
          <Route path="*" element={<Dashboard />} />
        </Routes>
      </main>
      <BottomNav role={user.role} />
    </div>
  </div>
);

const AdminApp = () => {
  const [user, setUser] = useState(null); // null = checking, false = signed out

  useEffect(() => {
    api.get("/admin/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setUser(false));
  }, []);

  const logout = useCallback(async () => {
    await api.post("/admin/auth/logout").catch(() => {});
    setUser(false);
  }, []);

  if (user === null) return <div className="min-h-screen bg-[#171717]" />;
  if (user === false) return <Login onSuccess={setUser} />;

  return (
    <AuthContext.Provider value={{ user, can: (perm) => can(user.role, perm) }}>
      <AdminShell user={user} onLogout={logout} />
    </AuthContext.Provider>
  );
};

export default AdminApp;
